import { useEffect, useMemo, useState } from 'react';
import { Chip } from '@coordio/ui';
import { sessionService } from '../../services/sessionService';
import type { ContextUsage } from '../../types/contextWindow';

export interface ContextUsageIndicatorProps {
  sessionId: string;
  /**
   * Polling interval in milliseconds. Default: 5000ms.
   */
  pollIntervalMs?: number;
}

/** Whether context-usage polling is enabled (controlled via VITE_CONTEXT_USAGE_POLLING_ENABLED). */
const isPollingEnabled = import.meta.env.VITE_CONTEXT_USAGE_POLLING_ENABLED === 'true';

/**
 * Displays context window usage (tokens and utilization) for a session.
 * Polling can be disabled by setting VITE_CONTEXT_USAGE_POLLING_ENABLED=false in the .env file.
 */
export const ContextUsageIndicator = ({ sessionId, pollIntervalMs = 5000 }: ContextUsageIndicatorProps) => {
  const [usage, setUsage] = useState<ContextUsage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const utilizationPct = useMemo(() => {
    if (!usage) return 0;
    const pct = Math.round((Number(usage.utilization) || 0) * 100);
    return Math.max(0, Math.min(100, pct));
  }, [usage]);

  const progressColor = useMemo(() => {
    if (!usage) return 'bg-[var(--color-accent)]';
    if (utilizationPct >= 90) return 'bg-red-500';
    if (utilizationPct >= 75) return 'bg-amber-500';
    return 'bg-[var(--color-accent)]';
  }, [usage, utilizationPct]);

  useEffect(() => {
    if (!isPollingEnabled) return;

    let isMounted = true;
    let timer: number | undefined;

    const load = async () => {
      try {
        const data = await sessionService.getContextUsage(sessionId);
        if (!isMounted) return;
        setUsage(data);
        setError(null);
      } catch (err: any) {
        if (!isMounted) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load context usage');
      }
    };

    load();
    timer = window.setInterval(load, pollIntervalMs);

    return () => {
      isMounted = false;
      if (timer) {
        window.clearInterval(timer);
      }
    };
  }, [sessionId, pollIntervalMs]);

  // When polling is disabled, show a minimal disabled indicator
  if (!isPollingEnabled) {
    return (
      <div>
        <span className="text-sm font-medium text-muted-foreground">
          Context usage polling disabled
        </span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Context usage</span>
        {usage?.shouldSummarize ? <Chip color="amber" label="Summarize suggested" /> : null}
      </div>

      <div className="h-2 w-full overflow-hidden rounded bg-[var(--color-bg-surface)]">
        <div
          className={`h-full rounded transition-all duration-300 ${progressColor}`}
          style={{ width: `${utilizationPct}%` }}
        />
      </div>

      <div className="flex justify-between mt-2">
        <span className="text-xs text-muted-foreground">
          {usage ? `${usage.totalTokens.toLocaleString()} / ${usage.maxContextTokens.toLocaleString()} tokens` : '—'}
        </span>
        <span className="text-xs text-muted-foreground">
          {usage ? `${utilizationPct}%` : ''}
        </span>
      </div>

      {usage ? (
        <span className="text-xs text-muted-foreground block mt-1">
          Threshold: {usage.summarizationThreshold.toLocaleString()} · Summary tokens: {usage.summaryTokens.toLocaleString()}
        </span>
      ) : null}

      {error ? (
        <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-400">
          {error}
        </div>
      ) : null}
    </div>
  );
};
